<template>
    <div 
        class="brick-root" 
        :class="[isActive ? 'active' : 'undefined', brickTypeName]"
        :data-jc-connection-id="connection.id"
        @mousedown="registerClickStart"
    >
        <brick-circle-base class="journey-brick" v-if="filterEnabled" @click="filterClick" v-tooltipped:[filterTooltip]="tooltipsEnabled" >
            <div class="brick-icon">
                <i class="fas fa-map-signs" />
            </div>
        </brick-circle-base>

        <brick-circle-base class="journey-brick" v-if="deduplicationTargetEnabled" @click="dedupTargetClick" v-tooltipped:[deduplicationTargetTooltip]="tooltipsEnabled">
            <div class="brick-icon">
                <img :src="DedupTargetIcon" />
            </div>
        </brick-circle-base>

        <brick-circle-base class="journey-brick" v-if="deduplicationBaseEnabled" @click="dedupBaseClick" v-tooltipped:[deduplicationBaseTooltip]="tooltipsEnabled">
            <div class="brick-icon">
                <img :src="DedupBaseIcon" />
            </div>
        </brick-circle-base>

        <brick-circle-base class="journey-brick" v-if="delayEnabled" @click="delayClick" v-tooltipped:[delayTooltip]="tooltipsEnabled">
            <div class="brick-icon">
                <i class="fas fa-clock" />
            </div>
        </brick-circle-base>

        <brick-circle-base class="journey-brick journey-filter-exclusion-checkpoint" v-if="filterExclusionEnabled"                      
            @click="filterExclusionClick" 
            v-tooltipped:[filterExclusionTooltip]="tooltipsEnabled" 
        >
            <div class="brick-icon">
                <span class="fa-stack fa-1x">
                    <i class="far fa-list-alt fa-stack-1x"></i>
                    <i class="fas fa-ban fa-stack-2x"></i>
                </span>
            </div>
        </brick-circle-base>
        
        <brick-circle-base class="journey-brick" @click="basicSettingsClick" v-tooltipped:[optionsTooltip]="tooltipsEnabled">
            <div class="brick-icon">
                <i class="fas fa-bars" />
            </div>
        </brick-circle-base>

        <component v-if="settingsOpen"
                    :is="editorType"
                    :connection="connection"
                    :config="config"
                    :is-new="isEditingNewCheckpoint"
                   
                    @save="save"
                    @hidden="closeSettings"
                    @change-status="statusChanged"
                    @delete-connection-checkpoint="deleteCheckpoint"
                    @delete-connection="deleteConnection"
        />
    </div>
</template>

<script>
    import resources from '@core/Util/Mixins/resources';
    import snowplow from "@modules/JourneyBuilder/snowplow";
    import {
        delayCheckpoint, 
        deduplicationTargetCheckpoint,
        deduplicationBaseCheckpoint,
        filterCheckpoint, 
        filterExclusionCheckpoint,
        optionsCheckpoint,
    } from "@modules/JourneyBuilder/Bricks/checkpoint-definitions";

    import journeyBrickMixin from '@modules/JourneyBuilder/Mixins/journey-brick'
    import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions';
    
    import tooltipped from "@modules/JourneyBuilder/directives/tooltipped";

    import BrickCircleBase from "@modules/JourneyBuilder/Components/Patterns/BrickCircleBase.vue";
    import Prompt from "@core/Components/Util/Prompt";
    
    import {
        ConnectionSettings,
        
        DedupBaseSettings,
        DedupTargetSettings,
        DelaySettings,
        FilterSettings,
        FilterExclusionSettings
    } from "./Settings";
    
    import DedupBaseIcon from "@core/Modules/JourneyBuilder/Assets/Checkpoints/icon_dedup_B.png";
    import DedupTargetIcon from "@core/Modules/JourneyBuilder/Assets/Checkpoints/icon_dedup_T.png";

    const nameToSettingsComponentMap = {
        [filterCheckpoint.name]: FilterSettings,
        [optionsCheckpoint.name]: ConnectionSettings,
        [delayCheckpoint.name]: DelaySettings,
        [deduplicationTargetCheckpoint.name]: DedupTargetSettings,
        [deduplicationBaseCheckpoint.name]: DedupBaseSettings,
        [filterExclusionCheckpoint.name]: FilterExclusionSettings
    };

    const hasMoved = (previous, current) => {
        const deltaX = previous.x - current.x;
        const deltaY = previous.y - current.y;

        return (deltaX * deltaX + deltaY * deltaY) > 10;
    };
    
    export default {
        name: 'brick-circle',
        components: {
            BrickCircleBase,
            Prompt
        },
        mixins: [journeyBrickMixin, permissionsMixin, resources],
        directives: {
            tooltipped
        },

        props: {
            connection: {
                type: Object,
                required: true
            },
            isDragging: {
                type: Boolean,
                required: false
            }
        },

        data() {
            return {
                settingsOpen: false,
                editorType: null,

                DedupBaseIcon,
                DedupTargetIcon,

                clickStart: {},
                isEditingNewCheckpoint: false
            };
        },
        
        computed: {
            filterEnabled(){
                return filterCheckpoint.isActiveForConnection(this.connection);
            },
            delayEnabled(){
                return delayCheckpoint.isActiveForConnection(this.connection);
            },
            deduplicationTargetEnabled(){
                return deduplicationTargetCheckpoint.isActiveForConnection(this.connection);
            },
            deduplicationBaseEnabled(){
                return deduplicationBaseCheckpoint.isActiveForConnection(this.connection);
            },
            filterExclusionEnabled(){
                return filterExclusionCheckpoint.isActiveForConnection(this.connection);
            },
            optionsTooltip() {
                return optionsCheckpoint.buildTooltip(this.connection);
            },
            filterTooltip(){
                return filterCheckpoint.buildTooltip(this.connection);
            },
            delayTooltip(){
                return delayCheckpoint.buildTooltip(this.connection);
            },
            deduplicationTargetTooltip(){
                return deduplicationTargetCheckpoint.buildTooltip(this.connection);
            },
            deduplicationBaseTooltip(){
                return deduplicationBaseCheckpoint.buildTooltip(this.connection);
            },
            filterExclusionTooltip(){
                return filterExclusionCheckpoint.buildTooltip(this.connection);
            },
            tooltipsEnabled(){
                return !this.isDragging;
            }
        },
        
        watch: {
            filterEnabled(enabled) {
                if(enabled) {
                    this.isEditingNewCheckpoint = true;
                    this.openSettings(filterCheckpoint.name);
                }
            },
            delayEnabled(enabled) {
                if(enabled) {
                    this.isEditingNewCheckpoint = true;
                    this.openSettings(delayCheckpoint.name);
                }
            },
            deduplicationTargetEnabled(enabled) {
                if(enabled) {
                    this.isEditingNewCheckpoint = true;
                    this.openSettings(deduplicationTargetCheckpoint.name);
                }
            },
            deduplicationBaseEnabled(enabled) {
                if(enabled) {
                    this.isEditingNewCheckpoint = true;
                    this.openSettings(deduplicationBaseCheckpoint.name);
                }
            }
        },

        methods: {
            async openSettings(tab) {
                if (nameToSettingsComponentMap.hasOwnProperty(tab)) {
                    this.editorType = nameToSettingsComponentMap[tab];
                }

                this.settingsOpen = true;
                snowplow.trackStructuredEvent('open-connection-settings');
            },
            registerClickStart(evt) {
                this.clickStart = {
                    x: evt.clientX,
                    y: evt.clientY
                };
            },
            closeSettings() {
                //Upon closing the settings, the next edited checkpoint may not be considered new anymore
                this.isEditingNewCheckpoint = false;
                this.settingsOpen = false;
            },
            statusChanged(settings) {
                this.connection.startDate = settings.activeFrom;
                this.connection.isDisabled = !settings.active;
                this.closeSettings();
            },
            deleteConnection($event){
                this.closeSettings();
                this.$emit('delete-connection', $event)
            },
            deleteCheckpoint($event){
                this.closeSettings();
                this.$emit('delete-connection-checkpoint', $event);  
            },
            save($event){
                this.closeSettings();
                this.$emit('change-connection', $event);
            },
            filterClick(evt) {
                if (!hasMoved(this.clickStart, { x: evt.clientX, y: evt.clientY })) {
                    this.openSettings(filterCheckpoint.name);
                }
            },
            delayClick(evt) {
                if (!hasMoved(this.clickStart, { x: evt.clientX, y: evt.clientY })) {
                    this.openSettings(delayCheckpoint.name);
                }
            },
            dedupTargetClick(evt) {
                if (!hasMoved(this.clickStart, { x: evt.clientX, y: evt.clientY })) {
                    this.openSettings(deduplicationTargetCheckpoint.name);
                }
            },
            dedupBaseClick(evt) {
                if (!hasMoved(this.clickStart, { x: evt.clientX, y: evt.clientY })) {
                    this.openSettings(deduplicationBaseCheckpoint.name);
                }
            },
            filterExclusionClick(evt){
                if (!hasMoved(this.clickStart, { x: evt.clientX, y: evt.clientY })) {
                    this.openSettings(filterExclusionCheckpoint.name);
                }
            },
            basicSettingsClick(evt){
                if (!hasMoved(this.clickStart, { x: evt.clientX, y: evt.clientY })) {
                    this.openSettings(optionsCheckpoint.name);
                }
            }
        }
    }
</script>

<style scoped lang="scss">
    @import "~@modules/JourneyBuilder/Style/journey-brick-circle.scss";
</style>